import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { TID } from "../../lib/testIds";

const Waitlist = () => {
    const [form, setForm] = useState({ name: "", organization: "", email: "" });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const onChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.organization || !form.email) {
            toast.error("Please fill in all fields");
            return;
        }
        setSubmitting(true);
        // simulated submission - no backend
        setTimeout(() => {
            setSubmitting(false);
            setSuccess(true);
            toast.success("You're on the waitlist", {
                description: `We'll reach out to ${form.email} soon.`,
            });
        }, 900);
    };

    return (
        <section
            id="waitlist"
            data-testid={TID.waitlistSection}
            className="relative py-28 md:py-36 px-6"
        >
            <div className="max-w-3xl mx-auto relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="relative rounded-3xl p-[1px] overflow-hidden"
                >
                    {/* Conic gradient border */}
                    <div
                        aria-hidden
                        className="absolute inset-0 rounded-3xl"
                        style={{
                            background:
                                "conic-gradient(from 120deg at 50% 50%, rgba(139,92,246,0.45), rgba(34,211,238,0.45), rgba(139,92,246,0.45))",
                        }}
                    />
                    <div className="relative rounded-3xl bg-white/95 backdrop-blur-2xl p-8 sm:p-12 dark:bg-[#06070F]/90">
                        <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0 rounded-3xl"
                            style={{
                                background:
                                    "radial-gradient(600px circle at 80% 0%, rgba(139,92,246,0.07), transparent 60%)",
                            }}
                        />

                        <div className="relative">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 border border-violet-100 font-mono text-[10px] tracking-[0.25em] uppercase text-violet-600 dark:bg-white/5 dark:border-white/10 dark:text-white/70">
                                <Sparkles size={12} />
                                Limited Early Access
                            </div>
                            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1] text-gradient-brand">
                                Join the future of education.
                            </h2>
                            <p className="mt-4 text-[#6B7280] dark:text-white/55 leading-relaxed">
                                Be among the first institutions to experience
                                Lambda Quest. We&apos;re onboarding a small,
                                hand-picked cohort.
                            </p>

                            <AnimatePresence mode="wait">
                                {!success ? (
                                    <motion.form
                                        key="form"
                                        onSubmit={onSubmit}
                                        initial={{ opacity: 1 }}
                                        exit={{ opacity: 0, y: -12 }}
                                        transition={{ duration: 0.4 }}
                                        className="mt-10 space-y-4"
                                    >
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <input
                                                name="name"
                                                value={form.name}
                                                onChange={onChange}
                                                placeholder="Your name"
                                                data-testid={TID.waitlistName}
                                                className="w-full rounded-xl bg-[#F9FAFB] border border-black/[0.08] px-5 py-4 text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20 transition dark:bg-white/[0.03] dark:border-white/10 dark:text-white dark:placeholder:text-white/30 dark:focus:border-brand-purple/60 dark:focus:ring-brand-purple/30"
                                            />
                                            <input
                                                name="organization"
                                                value={form.organization}
                                                onChange={onChange}
                                                placeholder="Organization"
                                                data-testid={TID.waitlistOrg}
                                                className="w-full rounded-xl bg-[#F9FAFB] border border-black/[0.08] px-5 py-4 text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20 transition dark:bg-white/[0.03] dark:border-white/10 dark:text-white dark:placeholder:text-white/30 dark:focus:border-brand-purple/60 dark:focus:ring-brand-purple/30"
                                            />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            value={form.email}
                                            onChange={onChange}
                                            placeholder="Work email"
                                            data-testid={TID.waitlistEmail}
                                            className="w-full rounded-xl bg-[#F9FAFB] border border-black/[0.08] px-5 py-4 text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20 transition dark:bg-white/[0.03] dark:border-white/10 dark:text-white dark:placeholder:text-white/30 dark:focus:border-brand-purple/60 dark:focus:ring-brand-purple/30"
                                        />
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            data-testid={TID.waitlistSubmit}
                                            className="btn-glow group inline-flex w-full items-center justify-center gap-2 rounded-full font-semibold px-7 py-4 transition-all duration-300 hover:scale-[1.01] hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-70
                                            bg-gradient-to-r from-violet-500 via-purple-500 to-cyan-400 text-white shadow-[0_4px_20px_rgba(139,92,246,0.35)]
                                            dark:[background-image:none] dark:bg-white dark:text-black dark:shadow-none"
                                        >
                                            {submitting
                                                ? "Joining..."
                                                : "Join Waitlist"}
                                            {!submitting && (
                                                <ArrowRight
                                                    size={18}
                                                    className="transition-transform group-hover:translate-x-1"
                                                />
                                            )}
                                        </button>
                                        <p className="text-center text-xs text-[#9CA3AF] dark:text-white/35 font-mono tracking-wider mt-2">
                                            No spam. No noise. Just the launch.
                                        </p>
                                    </motion.form>
                                ) : (
                                    <motion.div
                                        key="success"
                                        data-testid={TID.waitlistSuccess}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.6,
                                            ease: [0.16, 1, 0.3, 1],
                                        }}
                                        className="mt-10 flex flex-col items-center text-center"
                                    >
                                        <motion.div
                                            initial={{ scale: 0.6, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{
                                                duration: 0.6,
                                                type: "spring",
                                                stiffness: 200,
                                                damping: 18,
                                            }}
                                            className="relative flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-violet-100 to-cyan-50 border border-violet-200 dark:from-brand-purple/30 dark:to-brand-blue/30 dark:border-white/20 glow-purple"
                                        >
                                            <Check
                                                size={32}
                                                className="text-violet-600 dark:text-white"
                                            />
                                            <motion.span
                                                aria-hidden
                                                animate={{
                                                    scale: [1, 1.5, 1],
                                                    opacity: [0.6, 0, 0.6],
                                                }}
                                                transition={{
                                                    duration: 2.4,
                                                    repeat: Infinity,
                                                }}
                                                className="absolute inset-0 rounded-full border border-violet-400/50 dark:border-brand-purple/50"
                                            />
                                        </motion.div>
                                        <h3 className="mt-6 font-display text-2xl font-semibold text-[#111827] dark:text-white">
                                            You&apos;re on the list.
                                        </h3>
                                        <p className="mt-2 text-[#6B7280] dark:text-white/55 max-w-sm">
                                            We&apos;ll be in touch as we open the
                                            next cohort. Welcome to Lambda
                                            Quest.
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Waitlist;
